import React, { useEffect, useState } from 'react';
import { expensesAPI } from '../../utils/api';
import { formatDate, formatCurrency, todayInputDate } from '../../utils/helpers';
import Modal from '../../components/Modal';
import ConfirmModal from '../../components/ConfirmModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

const CATEGORIES = ['Rent', 'Electricity', 'Equipment', 'Salary', 'Maintenance', 'Miscellaneous'];
const CATEGORY_COLORS = {
  Rent: 'bg-blue-100 text-blue-700',
  Electricity: 'bg-yellow-100 text-yellow-700',
  Equipment: 'bg-purple-100 text-purple-700',
  Salary: 'bg-green-100 text-green-700',
  Maintenance: 'bg-orange-100 text-orange-700',
  Miscellaneous: 'bg-gray-100 text-gray-700',
};

const defaultForm = { category: 'Rent', amount: '', date: todayInputDate(), description: '', isRecurring: false };

function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(defaultForm);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const res = await expensesAPI.getAll(params);
      setExpenses(res.data);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); }, [filterCategory, dateRange]);

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (editTarget) {
        await expensesAPI.update(editTarget, form);
        toast.success('Expense updated!');
      } else {
        await expensesAPI.create(form);
        toast.success('Expense recorded!');
      }
      setModal(false);
      setEditTarget(null);
      setForm(defaultForm);
      fetchExpenses();
    } catch {
      toast.error('Failed to save expense');
    }
  };

  const handleEdit = (exp) => {
    setEditTarget(exp._id);
    setForm({
      category: exp.category, amount: exp.amount,
      date: exp.date.split('T')[0], description: exp.description, isRecurring: exp.isRecurring
    });
    setModal(true);
  };

  const handleDelete = async () => {
    try {
      await expensesAPI.delete(deleteModal.id);
      toast.success('Expense deleted');
      setDeleteModal({ open: false, id: null });
      fetchExpenses();
    } catch {
      toast.error('Delete failed');
    }
  };

  const total = expenses.reduce((s, e) => s + e.amount, 0);

  const byCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = expenses.filter(e => e.category === cat).reduce((s, e) => s + e.amount, 0);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Expenses</h2>
          <p className="text-gray-500 text-sm">Total shown: {formatCurrency(total)}</p>
        </div>
        <button onClick={() => { setEditTarget(null); setForm(defaultForm); setModal(true); }} className="btn-primary">
          + Add Expense
        </button>
      </div>

      {/* Category Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setFilterCategory(f => f === cat ? '' : cat)}
            className={`card py-3 text-center transition-all hover:shadow-md ${filterCategory === cat ? 'ring-2 ring-blue-500' : ''}`}
          >
            <p className="text-xs text-gray-500">{cat}</p>
            <p className="text-base font-bold text-gray-800 mt-0.5">{formatCurrency(byCategory[cat])}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card py-4">
        <div className="flex flex-wrap gap-4 items-center">
          <select className="input w-auto text-sm" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <input type="date" className="input w-auto text-sm" value={dateRange.start} onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))} placeholder="Start date" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" className="input w-auto text-sm" value={dateRange.end} onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))} />
          </div>
          {(filterCategory || dateRange.start || dateRange.end) && (
            <button onClick={() => { setFilterCategory(''); setDateRange({ start: '', end: '' }); }} className="text-sm text-blue-600 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? <LoadingSpinner /> : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">Category</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Description</th>
                  <th className="table-header">Recurring</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {expenses.map(exp => (
                  <tr key={exp._id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_COLORS[exp.category]}`}>
                        {exp.category}
                      </span>
                    </td>
                    <td className="table-cell font-bold text-red-600">{formatCurrency(exp.amount)}</td>
                    <td className="table-cell text-xs">{formatDate(exp.date)}</td>
                    <td className="table-cell text-sm text-gray-500 max-w-xs truncate">{exp.description || '-'}</td>
                    <td className="table-cell">
                      {exp.isRecurring ? <span className="text-xs text-blue-600 font-medium">Monthly</span> : <span className="text-xs text-gray-400">One-time</span>}
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-1">
                        <button onClick={() => handleEdit(exp)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg">✏️</button>
                        <button onClick={() => setDeleteModal({ open: true, id: exp._id })} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {expenses.length === 0 && (
              <div className="text-center py-12 text-gray-400">No expense records found</div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editTarget ? 'Edit Expense' : 'Add Expense'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Category *</label>
            <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} required>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Amount (₹) *</label>
              <input className="input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" required />
            </div>
            <div>
              <label className="label">Date *</label>
              <input className="input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
            </div>
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input resize-none" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional notes" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="recurring" checked={form.isRecurring} onChange={e => setForm(f => ({ ...f, isRecurring: e.target.checked }))} className="w-4 h-4 text-blue-600 rounded" />
            <label htmlFor="recurring" className="text-sm text-gray-700">Mark as recurring (monthly)</label>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editTarget ? 'Update' : 'Add Expense'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onConfirm={handleDelete}
        title="Delete Expense"
        message="Are you sure you want to delete this expense record?"
      />
    </div>
  );
}

export default Expenses;
